'use client';

import React, { useId, useRef, useState } from 'react';

import Popover, { PopoverProps } from '@mui/material/Popover';

import styles from './Tooltip.module.scss';
import { Box, Typography } from '@mui/material';
import classNames from 'classnames';

type TooltipTargetProps = {
	'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
	'aria-describedby'?: string;
	onMouseEnter?: React.MouseEventHandler<HTMLElement>;
	onMouseLeave?: React.MouseEventHandler<HTMLElement>;
};

export type TooltipProps = {
	target: React.ReactElement<TooltipTargetProps> | undefined;
	children: React.ReactNode;
} & Omit<PopoverProps, 'id' | 'open' | 'anchorEl' | 'children'>;

export const Tooltip: React.FC<TooltipProps> = ( {
	target,
	children,
	className,
	...popperProps
} ) => {
	const [ open, setOpen ] = useState( false );
	const anchorRef = useRef<HTMLElement | null>( null );

	const id = useId();

	const handlePopoverOpen = ( e: React.MouseEvent<HTMLElement> ) => setOpen( true );
	const handlePopoverClose = () => setOpen( false );

	/* Using React.cloneElement so we don't have to wrap a thing in a thing.
	Some MUI elements rely on their children being specific things.
	AvatarGroup needs to have Avatar components inside of it, so if we want
	tooltips on those Avatar components but keep them Avatar and not some
	other component we can use cloneElement */
	return (
		<Box className={ classNames( styles.container, className ) } ref={ anchorRef }>
			{ target
				? React.cloneElement( target, {
					'aria-haspopup': 'true',
					'aria-describedby': id,
					onMouseEnter: handlePopoverOpen,
					onMouseLeave: handlePopoverClose,
				  } )
				: null }
			<Popover
				id={ id }
				open={ open }
				anchorEl={ anchorRef.current }
				disableAutoFocus
				disableEnforceFocus
				disableRestoreFocus
				disableScrollLock={
					typeof popperProps.disableScrollLock === 'boolean'
						? popperProps.disableScrollLock
						: true
				}
				{ ...popperProps }
				anchorOrigin={ {
					vertical: 'bottom',
					horizontal: 'center',
					...popperProps.anchorOrigin,
				} }
				transformOrigin={ {
					vertical: 'top',
					horizontal: 'center',
					...popperProps.transformOrigin,
				} }
				slotProps={ {
					paper: {
						className: styles.tooltipPaper,
						...popperProps.slotProps?.paper,
					},
					...popperProps.slotProps,
				} }
				sx={ { pointerEvents: 'none' } }
			>
				{ typeof children === 'string' ? (
					<Typography className={ styles.text }>{ children }</Typography>
				) : (
					children
				) }
			</Popover>
		</Box>
	);
};
